import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [pendingUserId, setPendingUserId] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.requires_2fa) {
          setPendingUserId(data.user_id);
          setShow2FA(true);
          setLoading(false);
          return;
        }

        if (data.user.role !== 'admin') {
          toast.error('Acceso denegado. Se requiere rol de administrador');
          return;
        }

        localStorage.setItem('token', data.token);
        setUser(data.user);
        toast.success('Bienvenido, Administrador');
        navigate('/admin');
      } else {
        toast.error('Credenciales inválidas');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payloadCode = useBackup ? backupCode.trim() : totpCode;

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: pendingUserId, code: payloadCode, is_backup: useBackup })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token);
        setUser(data.user);
        toast.success('Bienvenido, Administrador');
        navigate('/admin');
      } else {
        toast.error(useBackup ? 'Código de recuperación inválido o ya usado' : 'Código incorrecto');
        if (useBackup) setBackupCode(''); else setTotpCode('');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  if (show2FA) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-full max-w-md p-8 border-2 border-black">
          <div className="text-center mb-6">
            <Shield className="w-12 h-12 mx-auto mb-3" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Verificación en Dos Pasos</h1>
            <p className="text-gray-600 mt-2 text-sm">
              {useBackup
                ? 'Introduce uno de tus códigos de recuperación'
                : 'Introduce el código de 6 dígitos de tu app autenticadora'}
            </p>
          </div>

          <form onSubmit={handleVerify2FA} className="space-y-6">
            {useBackup ? (
              <div>
                <Input
                  type="text"
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  className="text-center text-2xl tracking-widest font-mono h-16 border-2 border-black"
                  autoFocus
                  required
                  data-testid="backup-code-input"
                />
              </div>
            ) : (
              <div>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-3xl tracking-[0.5em] font-mono h-16 border-2 border-black"
                  autoFocus
                  required
                  data-testid="totp-input"
                />
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-black text-white hover:bg-gray-800 py-4"
              disabled={loading || (useBackup ? backupCode.replace(/-/g, '').length < 8 : totpCode.length !== 6)}
              data-testid="verify-2fa-btn"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </Button>

            <button
              type="button"
              onClick={() => { setUseBackup(!useBackup); setTotpCode(''); setBackupCode(''); }}
              className="w-full text-sm text-gray-600 hover:text-black underline"
              data-testid="toggle-backup-code-btn"
            >
              {useBackup ? 'Usar código de la app autenticadora' : '¿No tienes acceso? Usar código de recuperación'}
            </button>

            <button
              type="button"
              onClick={() => { setShow2FA(false); setTotpCode(''); setBackupCode(''); setUseBackup(false); setPendingUserId(''); }}
              className="w-full text-sm text-gray-500 hover:text-black"
            >
              Volver al login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-full max-w-md p-8 border-2 border-black">
        <h1 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'Playfair Display' }}>Panel de Administración</h1>
        <p className="text-center text-gray-600 mb-8">ARTHUR FITZ</p>
        
        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@arthurfitz.com"
              required
              data-testid="admin-email-input"
            />
          </div>
          
          <div>
            <Label>Contraseña</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="admin-password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-black text-white hover:bg-gray-800"
            disabled={loading}
            data-testid="admin-login-btn"
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </Button>

          <button
            type="button"
            onClick={async () => {
              const email = window.prompt('Introduce el email de la cuenta:');
              if (!email) return;
              try {
                await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email }),
                });
                toast.success('Si la cuenta existe, recibirás un enlace por email.');
              } catch {
                toast.error('Error de conexión');
              }
            }}
            className="w-full text-center text-xs text-gray-500 hover:text-black underline mt-2"
            data-testid="admin-forgot-password-link"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </form>
      </div>
    </div>
  );
}
